// src/pages/Community/LocationIndex.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import "./css/LocationIndex.css";
import "./css/ui-shell.css";
import ToolbarHeader, { GTA_GAME_TABS } from "./components/ToolbarHeader";
import Modal from "./components/Modal";
import LocationForm from "../Profile/LocationForm";

export default function LocationIndex({ user: propUser }) {
  const outlet = useOutletContext?.() || {};
  const user = propUser ?? outlet.user;

  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState("gta-5"); // tabs
  const [showModal, setShowModal] = useState(false);

  // src/pages/Community/LocationIndex.jsx
  useEffect(() => {
    let isActive = true;
    let unsub = null;

    const base = collection(db, "locations");

    const attach = (ref) =>
      onSnapshot(
        ref,
        (snap) => {
          if (!isActive) return;
          const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          // Always sort newest first on client to keep consistent UX
          docs.sort(
            (a, b) =>
              (b.createdAt?.toMillis?.() || 0) -
              (a.createdAt?.toMillis?.() || 0)
          );
          setLocations(docs);
          setLoading(false);
        },
        (err) => {
          const msg = String(err?.message || "");
          const indexErr =
            msg.includes("FAILED_PRECONDITION") ||
            msg.includes("failed-precondition");

          // If it's an index error on a filtered tab, retry without orderBy.
          if (indexErr && selectedKey !== "all" && selectedKey !== "mine") {
            if (unsub) {
              try {
                unsub();
              } catch { }
              unsub = null;
            }

            // Fallback: equality filters only (no orderBy = no composite index required)
            const filters = [
              where("isPrivate", "==", false),
              where("gameKey", "==", selectedKey),
            ];
            const fallbackRef = query(base, ...filters);
            unsub = attach(fallbackRef);
            return;
          }

          console.error("locations query error:", err);
          if (isActive) setLoading(false);
        }
      );

    setLoading(true);
    setLocations([]);

    let firstRef;
    if (selectedKey === "mine") {
      // Owner view (include private + public)
      if (user?.uid) {
        firstRef = query(base, where("authorUid", "==", user.uid));
      } else {
        firstRef = query(base, where("authorUid", "==", "__none__"));
      }
    } else if (selectedKey === "all") {
      // Public only; server-side order OK
      firstRef = query(
        base,
        where("isPrivate", "==", false),
        orderBy("createdAt", "desc")
      );
    } else {
      // Specific game: public only; try with orderBy then fallback on index error
      firstRef = query(
        base,
        where("isPrivate", "==", false),
        where("gameKey", "==", selectedKey),
        orderBy("createdAt", "desc")
      );
    }

    unsub = attach(firstRef);

    return () => {
      isActive = false;
      if (unsub) {
        try {
          unsub();
        } catch { }
      }
    };
  }, [selectedKey, user?.uid]);

  const list = useMemo(() => {
    let arr = [...locations];

    // Hide private items for everyone except the owner (and on the "mine" tab)
    if (selectedKey !== "mine") {
      arr = arr.filter((m) => {
        const ownerUid = m.authorUid ?? m.createdBy ?? m.createdByUid ?? null;
        const isOwner = user?.uid && ownerUid === user.uid;
        const isPrivate = Boolean(m.isPrivate ?? m.isHidden);
        return !isPrivate || isOwner;
      });
    }

    // existing search + sort...
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((m) =>
        [
          m.title,
          m.city,
          m.region,
          m.shortNote,
          ...(Array.isArray(m.tags) ? m.tags : []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    arr.sort((a, b) => {
      const an = (a.title || "").toLowerCase();
      const bn = (b.title || "").toLowerCase();
      if (an && bn && an !== bn) return an.localeCompare(bn);
      return (
        (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
      );
    });

    return arr;
  }, [locations, search, selectedKey, user?.uid]);

  return (
    <>
      <div className="loci-wrap">
        <ToolbarHeader
          children={
            <div className="forum-tabs">
              {GTA_GAME_TABS.concat(
                user ? [{ key: "mine", label: "My Locations" }] : []
              ).map(({ key, label }) => {
                const active = selectedKey === key;
                return (
                  <span
                    key={key}
                    className={`tab-text ${active ? "active" : ""}`}
                    onClick={() => setSelectedKey(key)}
                    role="button"
                    aria-current={active ? "page" : undefined}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          }
          search={search}
          setSearch={setSearch}
          placeholder="Search missions, tags, location…"
          // Use the primary button slot like characters:
          onAddMission={() => setShowModal(true)}
          addLabel="＋ Add Location"
          user={user}
        />

        <ul className="loci-grid">
          {loading &&
            Array.from({ length: 12 }).map((_, i) => (
              <li
                key={`sk-${i}`}
                className="loci-card loci-card--skeleton"
                aria-hidden="true"
              >
                <div className="loci-img sk-block" />
                <div className="loci-card-body">
                  <div className="loci-row">
                    <span className="loci-badge sk-chip" />
                    <span className="loci-tag sk-chip" />
                  </div>
                  <div className="sk-line sk-title" />
                  <div className="sk-line sk-meta" />
                  <div className="loci-tags">
                    <span className="loci-chip sk-chip" />
                    <span className="loci-chip sk-chip" />
                    <span className="loci-chip sk-chip" />
                  </div>
                </div>
              </li>
            ))}

          {!loading &&
            list.map((loc) => {
              const gameLabel =
                GTA_GAME_TABS.find((g) => g.key === loc.gameKey)?.label ||
                "Location";

              // ---- derive flags (back-compat) ----
              const isPrivate = Boolean(loc.isPrivate ?? loc.isHidden);
              const visLabel = isPrivate ? "Private" : "Public";
              // keep both classes so existing CSS (is-visible/is-hidden) still styles them
              const visClass = isPrivate
                ? "is-private is-hidden"
                : "is-public is-visible";

              const ownerUid =
                loc.authorUid ?? loc.createdBy ?? loc.createdByUid ?? null;
              const isOwner = !!user?.uid && ownerUid === user.uid;

              return (
                <li key={loc.id} className="loci-card">
                  {/* owner crown */}
                  {isOwner && (
                    <span
                      className="loci-owner"
                      title="Your location"
                      aria-label="Your location"
                    >
                      👑
                    </span>
                  )}

                  <Link
                    to={`/community/map_locations/${loc.id}`}
                    className="loci-card-link"
                  >
                    {loc.coverUrl ? (
                      <img
                        className="loci-img"
                        src={loc.coverUrl}
                        alt=""
                        loading="lazy"
                      />
                    ) : (
                      <div className="loci-img loci-img--placeholder">🗺️</div>
                    )}

                    <div className="loci-card-body">
                      <div className="loci-row">
                        <span className="loci-badge">{gameLabel}</span>

                        {/* visibility: Public / Private */}
                        <span className={`loci-tag ${visClass}`}>
                          {visLabel}
                        </span>
                      </div>

                      <h3 className="loci-name">
                        {loc.title || "Untitled location"}
                      </h3>

                      <div className="loci-meta">
                        {loc.city && <span>{loc.city}</span>}
                        {loc.region && <span> • {loc.region}</span>}
                        {loc.difficultyToFind && (
                          <span> • {loc.difficultyToFind}</span>
                        )}
                        {loc.coordsPct?.xPct != null &&
                          loc.coordsPct?.yPct != null && (
                            <span>
                              {" "}
                              • {Number(loc.coordsPct.xPct).toFixed(1)}%,{" "}
                              {Number(loc.coordsPct.yPct).toFixed(1)}%
                            </span>
                          )}
                      </div>

                      {Array.isArray(loc.tags) && loc.tags.length > 0 && (
                        <div className="loci-tags">
                          {loc.tags.slice(0, 4).map((t, i) => (
                            <span key={i} className="loci-chip">
                              #{t}
                            </span>
                          ))}
                          {loc.tags.length > 4 && (
                            <span className="loci-chip">
                              +{loc.tags.length - 4}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </Link>
                </li>
              );
            })}

          {!loading && list.length === 0 && (
            <div className="loci-empty">No locations match your search.</div>
          )}
        </ul>

        {/* Modal for Add Location */}
        <Modal
          open={showModal}
          onClose={() => setShowModal(false)}
          title="Add a Location"
          size="md"
          closeOnBackdrop={true}
          showClose={true}
        >
          <LocationForm
            user={user}
            role={user?.role}
            defaultGameKey={selectedKey === "all" ? "gta-6" : selectedKey}
            onDone={() => setShowModal(false)}
            onCancel={() => setShowModal(false)}
          />
        </Modal>
      </div>
    </>
  );
}
